import { useEffect, useState } from "react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { VoiceRecordingsTable } from "@/components/tools/VoiceRecordingsTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useGetUsers } from "@/hooks/queries/useGetUsers";
import { useGetVoiceRecordings } from "@/hooks/queries/useGetVoiceRecordings";
import { useSetTitle } from "@/hooks/useSetTitle";
import { ALL_USERS_KEY } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { UserRole } from "@/types";

export const ToolsVoiceRecordingsListPage = () => {
  /* React Hooks */
  const [selectedUserId, setSelectedUserId] = useState<string>(ALL_USERS_KEY);

  /* Database CRUD Operations */
  // Read
  const { data: users } = useGetUsers();
  const { data: recordings, status } = useGetVoiceRecordings({
    userId:
      selectedUserId === ALL_USERS_KEY ? undefined : Number(selectedUserId),
  });

  useSetTitle({ title: "Tools | Voice Recordings" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.VOICE_RECORDINGS);
  }, []);

  /* Helpers */
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const staffers = (users || []).filter(
    (user) => user.userRole === UserRole.STAFFER,
  );

  /* Render */
  const render = () => {
    if (status === "pending") return <LoadingIndicator variant="medium" />;
    if (status === "error")
      return <ErrorText errorMessage="Error loading voice recordings." />;
    return <VoiceRecordingsTable recordings={recordings} />;
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Voice Recordings</TopbarTitle>
        <Select onValueChange={setSelectedUserId} value={selectedUserId}>
          <SelectTrigger className="w-[220px] rounded-full">
            <SelectValue placeholder="All staffers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_USERS_KEY}>All staffers</SelectItem>
            {staffers.map((staffer) => (
              <SelectItem key={staffer.id} value={staffer.id.toString()}>
                {staffer.firstName} {staffer.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TopbarContainer>
      <Column className="flex-1 overflow-hidden p-6">{render()}</Column>
    </Column>
  );
};
